import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { notOrderingApi } from "../../api/notOrdering.js";
import SearchBar from "../../components/SearchBar.jsx";
import Pagination from "../../components/Pagination.jsx";
import ColumnFilterHeader from "../../components/ColumnFilterHeader.jsx";
import { Download, Loader2, X } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");
const formatAssigneeValue = (v) => (v === null ? "Unassigned" : v);
const formatDateValue = (v) => (v === null ? "(empty)" : fmtDate(v));

// Assignment to CRM users is fully automatic (nightly segmentation job) --
// admins are oversight-only here: they can review the list and manually pull
// a customer out of the bucket, dropping them from that user's Not Ordering
// queue immediately (same not_ordering flag drives both views).
export default function NotOrderingAdminPage() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  // Google-Sheets-style column-header filters
  const [nameFilter, setNameFilter] = useState([]);
  const [assignedToFilter, setAssignedToFilter] = useState([]);
  const [lastOrderFilter, setLastOrderFilter] = useState([]);
  const [sinceFilter, setSinceFilter] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);

  const buildFilterParams = useCallback((searchTerm) => {
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (nameFilter.length) params.name_in = nameFilter.join(",");
    if (assignedToFilter.length) params.assigned_to_in = assignedToFilter.map((v) => v ?? "null").join(",");
    if (lastOrderFilter.length) params.last_order_in = lastOrderFilter.map((v) => v ?? "null").join(",");
    if (sinceFilter.length) params.not_ordering_since_in = sinceFilter.map((v) => v ?? "null").join(",");
    return params;
  }, [nameFilter, assignedToFilter, lastOrderFilter, sinceFilter]);

  const fetchList = useCallback(async (searchTerm, pageNum, pageLimit) => {
    setLoading(true);
    try {
      const params = { limit: pageLimit, page: pageNum, ...buildFilterParams(searchTerm) };
      const res = await notOrderingApi.getList(params);
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotalCount(typeof res?.count === "number" ? res.count : (res?.data?.length || 0));
      setTotalPages(res?.totalPages || 1);
    } catch (err) {
      toast.error(err.response?.status === 403 ? (err.response?.data?.error || "Permission denied") : "Failed to load Not Ordering list");
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  useEffect(() => {
    fetchList(debouncedSearch, page, limit);
  }, [fetchList, debouncedSearch, page, limit]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, nameFilter, assignedToFilter, lastOrderFilter, sinceFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = buildFilterParams(debouncedSearch);
      const res = await notOrderingApi.exportList(params);
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `not-ordering-list_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleRemove = async (lead) => {
    if (!window.confirm(`Remove "${lead.name}" from the Not Ordering list?\nThey'll be dropped from ${lead.NotOrderingAssignee?.name || "the assigned agent"}'s queue and become eligible for the regular follow-up queue again.`)) {
      return;
    }
    setRemovingId(lead.id);
    try {
      await notOrderingApi.remove(lead.id);
      toast.success(`Removed "${lead.name}" from Not Ordering.`);
      setRows((prev) => prev.filter((r) => r.id !== lead.id));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove customer.");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      {!loading && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 8 }}>
          {totalCount} customer{totalCount !== 1 ? "s" : ""} inactive &gt;3 months or never ordered — auto-assigned to CRM users
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search by name or phone…" style={{ maxWidth: 360 }} />
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={exporting || totalCount === 0}
          onClick={handleExport}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto" }}
        >
          {exporting ? <Loader2 size={14} className="spin" /> : <Download size={14} />} Export CSV
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 0", color: "var(--text-muted)", fontSize: 13 }}>
          <Loader2 size={18} className="spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-muted)", fontSize: 13 }}>
          {search ? `No results for "${search}"` : "Nobody currently matches the >3 months / never-ordered criteria"}
        </div>
      ) : (
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid var(--border)", boxShadow: "var(--shadow)", opacity: loading ? 0.6 : 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--card)", borderBottom: "2px solid var(--border)" }}>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <ColumnFilterHeader label="Customer" field="name" selected={nameFilter} onApply={setNameFilter} fetchFn={notOrderingApi.getColumnValues} />
                </th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Phone</th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <ColumnFilterHeader label="Last Order" field="last_ordered_at" selected={lastOrderFilter} onApply={setLastOrderFilter} fetchFn={notOrderingApi.getColumnValues} formatValue={formatDateValue} />
                </th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <ColumnFilterHeader label="Not Ordering Since" field="not_ordering_since" selected={sinceFilter} onApply={setSinceFilter} fetchFn={notOrderingApi.getColumnValues} formatValue={formatDateValue} />
                </th>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  <ColumnFilterHeader label="Assigned To" field="assigned_to" selected={assignedToFilter} onApply={setAssignedToFilter} fetchFn={notOrderingApi.getColumnValues} formatValue={formatAssigneeValue} />
                </th>
                <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((lead, idx) => (
                <tr
                  key={lead.id}
                  onClick={() => navigate(`/customers/${lead.id}`)}
                  style={{ borderBottom: "1px solid var(--border)", background: idx % 2 === 0 ? "var(--card)" : "var(--bg)", cursor: "pointer" }}
                >
                  <td style={{ padding: "10px 14px", fontWeight: 600 }}>{lead.name}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                    {lead.phone || <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    {fmtDate(lead.last_ordered_at) || <span style={{ color: "var(--text-muted)" }}>Never</span>}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    {fmtDate(lead.not_ordering_since) || <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>
                    {lead.NotOrderingAssignee?.name || <span style={{ color: "var(--text-muted)" }}>Unassigned</span>}
                  </td>
                  <td style={{ padding: "10px 14px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={removingId === lead.id}
                      onClick={() => handleRemove(lead)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
                    >
                      {removingId === lead.id ? <><Loader2 size={13} className="spin" /> Removing…</> : <><X size={13} /> Remove</>}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > 0 && (
        <Pagination page={page} pages={totalPages} limit={limit} onLimitChange={setLimit} onChange={setPage} />
      )}
    </div>
  );
}
